import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Search,
    Plus,
    ChevronRight,
    Check,
    X,
    Trash2,
    Camera,
    Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { Money, StatusBadge, VerifiedBadge, Chip, Stars, ListShell } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

const FILTERS = ["All", "Approved", "Pending", "Rejected"];

function EditDoctorSheet({ initial, onClose, onSaved }) {
    const isNew = !initial?.doctor_id;
    const fileRef = useRef(null);
    const [f, setF] = useState({
        name: initial?.name || "",
        email: initial?.email || "",
        password: "",
        specialty: initial?.specialty || "",
        experience_years: initial?.experience_years ?? 0,
        qualifications: initial?.qualifications || "",
        bio: initial?.bio || "",
        consultation_fee: initial?.consultation_fee ?? 500,
        avatar_url: initial?.avatar_url || "",
        status: initial?.status || "pending",
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    function set(k, v) {
        setF((p) => ({ ...p, [k]: v }));
    }
    function pickImage(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Under 3 MB please.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => set("avatar_url", reader.result);
        reader.readAsDataURL(file);
    }

    async function save() {
        setSaving(true);
        try {
            if (isNew) {
                if (!f.password || f.password.length < 6) {
                    toast.error("Password must be at least 6 characters.");
                    setSaving(false);
                    return;
                }
                await api.post("/doctors", {
                    ...f,
                    experience_years: Number(f.experience_years),
                    consultation_fee: Number(f.consultation_fee),
                });
                toast.success("Doctor created (pending approval).");
            } else {
                const { email, password, ...updatable } = f;
                await api.put(`/doctors/${initial.doctor_id}`, {
                    ...updatable,
                    experience_years: Number(updatable.experience_years),
                    consultation_fee: Number(updatable.consultation_fee),
                });
                toast.success("Saved.");
            }
            onSaved();
            onClose();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not save.");
        } finally {
            setSaving(false);
        }
    }
    async function remove() {
        if (!window.confirm(`Delete ${f.name}?`)) return;
        setDeleting(true);
        try {
            await api.delete(`/doctors/${initial.doctor_id}`);
            toast.success("Deleted.");
            onSaved();
            onClose();
        } catch {
            toast.error("Could not delete.");
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end justify-center bg-veda-inverse/40 backdrop-blur-sm animate-fade-in sm:items-center"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="max-h-[90vh] w-full max-w-md overflow-y-auto border-t border-veda-border bg-veda-surface p-5 sm:border">
                <div className="flex items-center justify-between">
                    <p className="font-display text-2xl text-veda-onSurface">
                        {isNew ? "New doctor" : "Edit doctor"}
                    </p>
                    <button onClick={onClose} className="p-1 text-veda-muted">
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-4 flex items-center gap-3">
                    <div className="h-20 w-20 shrink-0 overflow-hidden border border-veda-border bg-veda-surface2">
                        {f.avatar_url ? (
                            <img
                                src={f.avatar_url}
                                alt=""
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <div className="grid h-full w-full place-items-center text-veda-muted">
                                <Camera size={16} />
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => fileRef.current?.click()}
                        className="border border-veda-border bg-veda-surface px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em]"
                    >
                        {f.avatar_url ? "Change" : "Upload"}
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={pickImage}
                    />
                </div>

                <div className="mt-4 flex flex-col gap-3">
                    <input
                        placeholder="Name"
                        value={f.name}
                        onChange={(e) => set("name", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <input
                        placeholder="Email"
                        value={f.email}
                        disabled={!isNew}
                        onChange={(e) => set("email", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm disabled:opacity-70"
                    />
                    {isNew && (
                        <input
                            placeholder="Password (min 6 chars)"
                            type="password"
                            value={f.password}
                            onChange={(e) => set("password", e.target.value)}
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        />
                    )}
                    <input
                        placeholder="Specialty"
                        value={f.specialty}
                        onChange={(e) => set("specialty", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <input
                            type="number"
                            placeholder="Experience (yrs)"
                            value={f.experience_years}
                            onChange={(e) =>
                                set("experience_years", e.target.value)
                            }
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        />
                        <input
                            type="number"
                            placeholder="Fee (₹)"
                            value={f.consultation_fee}
                            onChange={(e) =>
                                set("consultation_fee", e.target.value)
                            }
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        />
                    </div>
                    <input
                        placeholder="Qualifications"
                        value={f.qualifications}
                        onChange={(e) => set("qualifications", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <textarea
                        rows={3}
                        placeholder="Bio"
                        value={f.bio}
                        onChange={(e) => set("bio", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    {!isNew && (
                        <select
                            value={f.status}
                            onChange={(e) => set("status", e.target.value)}
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    )}
                </div>

                <div className="mt-5 flex items-center gap-2">
                    <button
                        onClick={save}
                        disabled={saving}
                        className="inline-flex flex-1 items-center justify-center gap-2 bg-veda-brand px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-white disabled:opacity-70"
                    >
                        {saving && <Loader2 size={13} className="animate-spin" />}
                        Save
                    </button>
                    {!isNew && (
                        <button
                            onClick={remove}
                            disabled={deleting}
                            className="inline-flex items-center gap-1 border border-veda-error px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-veda-error"
                        >
                            <Trash2 size={13} /> Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AdminDoctors() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("All");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== "All") params.status = filter.toLowerCase();
            const { data } = await api.get("/doctors", { params });
            setItems(data);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return items;
        return items.filter(
            (d) =>
                d.name?.toLowerCase().includes(t) ||
                d.specialty?.toLowerCase().includes(t) ||
                d.email?.toLowerCase().includes(t),
        );
    }, [items, q]);

    async function setStatus(d, status) {
        // optimistic
        setItems((prev) =>
            prev.map((x) =>
                x.doctor_id === d.doctor_id
                    ? { ...x, status, verified: status === "approved" }
                    : x,
            ),
        );
        try {
            await api.put(`/doctors/${d.doctor_id}`, { status });
            toast.success(`Marked as ${status}.`);
            if (filter !== "All") load();
        } catch {
            toast.error("Could not update.");
            load();
        }
    }

    let listBody;
    if (loading || filtered.length === 0) {
        listBody = (
            <ListShell
                loading={loading}
                isEmpty={!loading && filtered.length === 0}
                emptyMessage="No doctors found."
            />
        );
    } else {
        listBody = (
            <ul className="divide-y divide-veda-divider">
                {filtered.map((d) => (
                    <li
                        key={d.doctor_id}
                        className="grid grid-cols-[56px_1fr_auto] items-start gap-3 py-3"
                    >
                        <img
                            src={d.avatar_url}
                            alt=""
                            className="h-14 w-14 object-cover"
                        />
                        <div className="min-w-0">
                            <p className="line-clamp-1 text-sm font-semibold text-veda-onSurface">
                                {d.title ? `${d.title} ` : ""}{d.name}
                            </p>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-veda-muted">
                                {d.specialty} · {d.experience_years}y
                                {d.clinic_name && (
                                    <span> · {d.clinic_name}</span>
                                )}
                            </p>
                            {d.practice_locations?.length > 0 && (
                                <p className="mt-0.5 text-[10px] text-veda-muted">
                                    Practices in: {d.practice_locations.join(", ")}
                                </p>
                            )}
                            {d.license_number && (
                                <p className="mt-0.5 text-[10px] text-veda-muted">
                                    License: {d.license_number}
                                </p>
                            )}
                            <div className="mt-1 flex items-center gap-2">
                                <StatusBadge status={d.status} />
                                {d.verified && (
                                    <VerifiedBadge
                                        testid={TID.verifiedBadge(d.doctor_id)}
                                    />
                                )}
                                <span className="text-[10px] text-veda-muted">
                                    <Money value={d.consultation_fee} />
                                </span>
                            </div>
                            {(d.degree_document || d.license_document) && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {d.degree_document && (
                                        <a
                                            href={d.degree_document}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`review-degree-${d.doctor_id}`}
                                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface2 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                                        >
                                            Degree doc
                                        </a>
                                    )}
                                    {d.license_document && (
                                        <a
                                            href={d.license_document}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`review-license-${d.doctor_id}`}
                                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface2 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                                        >
                                            License doc
                                        </a>
                                    )}
                                </div>
                            )}
                            {d.status !== "approved" && (
                                <div className="mt-2 flex gap-2">
                                    <button
                                        data-testid={TID.approve(d.doctor_id)}
                                        onClick={() => setStatus(d, "approved")}
                                        className="inline-flex items-center gap-1 border border-veda-success bg-veda-success px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                                    >
                                        <Check size={10} /> Approve
                                    </button>
                                    <button
                                        data-testid={TID.reject(d.doctor_id)}
                                        onClick={() => setStatus(d, "rejected")}
                                        className="inline-flex items-center gap-1 border border-veda-error px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-error"
                                    >
                                        <X size={10} /> Not approve
                                    </button>
                                </div>
                            )}
                            {d.status === "approved" && (
                                <div className="mt-2">
                                    <button
                                        data-testid={`revoke-${d.doctor_id}`}
                                        onClick={() => setStatus(d, "rejected")}
                                        className="inline-flex items-center gap-1 border border-veda-error px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-error"
                                    >
                                        <X size={10} /> Revoke
                                    </button>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setEditing(d)}
                            className="grid h-8 w-8 place-items-center border border-veda-border"
                            aria-label="Edit"
                        >
                            <ChevronRight size={15} />
                        </button>
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <div data-testid={TID.adminDoctors} className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
                <div className="flex flex-1 items-center border border-veda-border bg-veda-surface">
                    <Search size={14} className="ml-3 text-veda-muted" />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search doctors"
                        className="flex-1 bg-transparent px-2 py-2.5 text-sm focus:outline-none"
                    />
                </div>
                <button
                    onClick={() => setEditing("new")}
                    className="inline-flex items-center gap-1 bg-veda-inverse px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onInverse"
                >
                    <Plus size={13} /> New
                </button>
            </div>

            <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
                {FILTERS.map((f) => (
                    <Chip
                        key={f}
                        active={filter === f}
                        onClick={() => setFilter(f)}
                    >
                        {f}
                    </Chip>
                ))}
            </div>

            {listBody}

            {editing && (
                <EditDoctorSheet
                    initial={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={load}
                />
            )}
        </div>
    );
}
